<template>
  <div
    class="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-dark-bg p-4 transition-colors duration-500 relative overflow-hidden"
  >
    <!-- Background Decorative Elements -->
    <div
      class="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
    ></div>
    <div
      class="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
    ></div>

    <div
      class="w-full max-w-[1100px] grid lg:grid-cols-2 bg-white dark:bg-dark-surface rounded-[40px] shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden relative z-10 border border-slate-100 dark:border-white/5"
    >
      <!-- Left Side: Login Form -->
      <div class="p-8 md:p-16 flex flex-col justify-center">
        <!-- Logo/Brand -->
        <div class="mb-10 flex items-center gap-3">
          <div
            class="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center text-white text-2xl shadow-lg shadow-primary/30"
          >
            🚗
          </div>
          <span
            class="text-2xl font-black tracking-tighter dark:text-white uppercase"
            >Ridey <span class="text-primary">Admin</span></span
          >
        </div>

        <div class="mb-8">
          <h1 class="text-3xl font-black text-slate-800 dark:text-white mb-2">
            {{ $t("login_title") }}
          </h1>
          <p
            class="text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-[10px]"
          >
            {{ $t("login_subtitle") }}
          </p>
        </div>

        <form @submit.prevent="submitLogin" class="space-y-6">
          <div class="space-y-2">
            <label
              class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1"
              >{{ $t("identifier_label") }}</label
            >
            <div class="relative group">
              <span
                class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                >👤</span
              >
              <input
                v-model="email"
                type="text"
                class="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-dark-input border border-slate-100 dark:border-white/5 rounded-2xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:text-white font-bold text-sm"
                placeholder="admin@example.com or 010..."
                required
              />
            </div>
          </div>

          <div class="space-y-2">
            <div class="flex justify-between items-center px-1">
              <label
                class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
                >{{ $t("password_label") }}</label
              >
              <a
                href="#"
                class="text-[10px] font-black text-primary uppercase tracking-widest hover:underline"
                >{{ $t("forgot_password") }}</a
              >
            </div>
            <div class="relative group">
              <span
                class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                >🔒</span
              >
              <input
                v-model="password"
                type="password"
                class="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-dark-input border border-slate-100 dark:border-white/5 rounded-2xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:text-white font-bold text-sm"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-primary hover:bg-primary-hover text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/30 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <span
              v-if="loading"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            ></span>
            {{ loading ? $t("logging_in") : $t("login_button") }}
          </button>
        </form>

        <div
          v-if="error"
          class="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3"
        >
          <span class="text-xl">⚠️</span>
          <p class="text-xs font-bold text-red-500">{{ error }}</p>
        </div>

        <div
          class="mt-10 pt-8 border-t dark:border-white/5 flex flex-col items-center gap-4"
        >
          <p
            class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
          >
            {{ $t("login_footer") }}
            <a href="#" class="text-primary hover:underline">{{
              $t("contact_admin")
            }}</a>
          </p>

          <!-- Language & Theme Switchers -->
          <div class="flex items-center gap-4">
            <button
              @click="toggleTheme"
              class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-dark-input flex items-center justify-center text-lg hover:scale-110 transition-transform"
            >
              {{ isDark ? "☀️" : "🌙" }}
            </button>
            <button
              @click="toggleLocale"
              class="px-3 py-2 rounded-xl bg-slate-100 dark:bg-dark-input text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-transform dark:text-white"
            >
              {{ locale === "en" ? "AR" : "EN" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Right Side: Decorative/Image -->
      <div class="hidden lg:block relative bg-primary overflow-hidden">
        <div
          class="absolute inset-0 bg-gradient-to-br from-primary to-blue-700 opacity-90"
        ></div>
        <!-- Abstract Shapes -->
        <div class="absolute top-0 left-0 w-full h-full">
          <div
            class="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"
          ></div>
          <div
            class="absolute bottom-20 right-20 w-80 h-80 bg-black/10 rounded-full blur-3xl"
          ></div>
        </div>

        <div
          class="relative h-full flex flex-col items-center justify-center p-16 text-white text-center"
        >
          <div
            class="mb-8 p-6 bg-white/10 backdrop-blur-xl rounded-[40px] border border-white/20 shadow-2xl scale-110"
          >
            <div class="text-6xl mb-4">🛡️</div>
            <h2
              class="text-3xl font-black mb-2 tracking-tighter uppercase italic"
            >
              {{ $t("secure_access") }}
            </h2>
            <p
              class="text-xs font-bold uppercase tracking-[0.2em] text-white/70"
            >
              {{ $t("fleet_management_system") }}
            </p>
          </div>
          <div class="max-w-xs">
            <p class="text-sm font-medium leading-relaxed opacity-80 italic">
              "{{ $t("login_quote") }}"
            </p>
          </div>

          <!-- Decorative Car Icon -->
          <div class="mt-12 text-8xl opacity-20 select-none">🏎️</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from "@/services/auth.service";
import { isDark, toggleTheme } from "@/utils/theme";
import { useI18n } from "vue-i18n";

export default {
  name: "Login",
  setup() {
    const { locale } = useI18n();
    const toggleLocale = () => {
      locale.value = locale.value === "en" ? "ar" : "en";
    };
    return { isDark, toggleTheme, locale, toggleLocale };
  },
  data() {
    return {
      email: "",
      password: "",
      error: "",
      loading: false,
    };
  },
  methods: {
    async submitLogin() {
      this.loading = true;
      this.error = "";

      try {
        await authService.login({
          email: this.email,
          password: this.password,
        });

        await this.$router.push({ name: "Dashboard" });
      } catch (err) {
        this.error = err.response?.data?.message || this.$t("login_failed_msg");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dark-bg {
  background-color: #0b0d14;
}
</style>
